import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import { useParams } from 'react-router-dom'
import useDirectory from 'hooks/useDirectory'
import useOverflow from 'hooks/useOverflow'
import { Building } from 'models/Building'
import useSWR from 'swr'

import CustomModal from 'components/atoms/Modal'
import Spinner from 'components/atoms/Spinner'

import 'quill/dist/quill.snow.css'

export default function BusinessInfoModal({
  setOpen
}: {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { t } = useTranslation()
  const { id: buildingId } = useParams()
  const { directory } = useDirectory()
  const isCommunity = directory === 'community'
  const apiPrefix = isCommunity ? '/v1/tour' : '/v1'
  const isMobileScreen = useMediaQuery({ query: '(max-width: 768px)' })
  const qlContainerRef = useRef<HTMLDivElement>(null)
  const { hasOverflow, elementRef } = useOverflow()

  const {
    data: buildingInfo,
    isLoading,
    isValidating
  } = useSWR<Building>({
    url: `${apiPrefix}/buildings/${buildingId}`
  })

  const [isReady, setIsReady] = useState(false)
  const [hasDescription, setHasDescription] = useState(false)
  const [hasImage, setHasImage] = useState(false)
  // Lưu HTML content để inject vào DOM sau khi isReady = true
  const descriptionHtmlRef = useRef<string>('')

  // Reset khi mở modal cho building mới
  useEffect(() => {
    setIsReady(false)
    setHasDescription(false)
    setHasImage(false)
    descriptionHtmlRef.current = ''
  }, [buildingId])

  const handleFetchContent = useCallback(async () => {
    if (!buildingInfo || isValidating) return

    const descriptionUrl = buildingInfo?.extraInfo?.descriptionUrl
    const imageUrl = buildingInfo?.extraInfo?.businessImageUrl

    const preloadImage = (src: string): Promise<void> =>
      new Promise((resolve) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => resolve()
        img.onerror = () => resolve()
        img.src = src
      })

    try {
      const tasks: Promise<void>[] = []

      // Fetch description — dùng DOMParser để check content
      // KHÔNG inject vào qlContainerRef ở đây vì ref chưa được mount
      if (descriptionUrl) {
        tasks.push(
          (async () => {
            const response = await fetch(descriptionUrl)
            const text = await response.text()
            // Dùng DOMParser để lấy plainText mà không cần DOM ref
            const doc = new DOMParser().parseFromString(text, 'text/html')
            const plainText = doc.body.innerText?.trim() ?? ''
            if (plainText !== '') {
              descriptionHtmlRef.current = text
              setHasDescription(true)
            }
          })()
        )
      }

      if (imageUrl) {
        tasks.push(
          preloadImage(imageUrl).then(() => {
            setHasImage(true)
          })
        )
      }

      await Promise.all(tasks)
    } catch (_) {
      // ignore
    } finally {
      setIsReady(true)
    }
  }, [buildingInfo, isValidating])

  useEffect(() => {
    handleFetchContent()
  }, [handleFetchContent])

  // Sau khi isReady = true và qlContainerRef được mount,
  // inject HTML content vào DOM
  useEffect(() => {
    if (isReady && hasDescription && qlContainerRef.current) {
      qlContainerRef.current.innerHTML = descriptionHtmlRef.current
    }
  }, [isReady, hasDescription])

  const handleClose = () => setOpen(false)

  return (
    <CustomModal
      width={isMobileScreen ? '100vw' : '90vw'}
      bodyStyle={{
        height: isMobileScreen ? '100vh' : '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}
      rootClassName="custom-annotation-detail-modal"
      maskClosable
      onCancel={handleClose}
      open
      footer={null}
      destroyOnClose
      centered
    >
      <div
        className={`absolute top-3 right-3 cursor-pointer z-50 text-white leading-none select-none ${
          isMobileScreen ? 'text-md' : 'text-2xl'
        }`}
        onClick={handleClose}
      >
        ×
      </div>

      <Spinner
        spinning={isLoading || !isReady}
        className="w-full h-full overflow-hidden"
      >
        {isReady && (
          <div className="flex flex-col h-full overflow-hidden">
            {!hasImage && !hasDescription && (
              <div className="flex justify-center items-center w-full h-[275px] text-white">
                {t('building.noBusinessInfo')}
              </div>
            )}

            <div className={isMobileScreen ? 'mx-2' : 'mx-5'}>
              {/* Có cả image lẫn description */}
              {hasImage && hasDescription && (
                <div className="annotation-media-memo-layout">
                  {/* Media column */}
                  <div className="annotation-media-col annotation-media-col-image">
                    <div className="flex justify-center relative">
                      <div className="hidden md:flex w-full overflow-auto items-center justify-center max-h-[87vh]">
                        <img
                          crossOrigin="anonymous"
                          src={buildingInfo?.extraInfo?.businessImageUrl}
                          alt="businessLogoImage"
                          style={{
                            width: '100%',
                            height: 'auto',
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            objectPosition: 'center'
                          }}
                          loading="eager"
                          decoding="sync"
                          className="shadow-md"
                        />
                      </div>
                      <img
                        crossOrigin="anonymous"
                        src={buildingInfo?.extraInfo?.businessImageUrl}
                        alt="businessLogoImage"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                          objectPosition: 'center'
                        }}
                        loading="eager"
                        decoding="sync"
                        className="shadow-md md:hidden"
                      />
                    </div>
                  </div>

                  {/* Text column */}
                  <div className="annotation-text-col annotation-text-col-image">
                    <div
                      ref={elementRef}
                      className={`ql-container overflow-y-auto memo-container !h-auto pt-1 text-white w-full ${
                        hasOverflow ? 'scrollbar-visible' : 'scrollbar-hidden'
                      }`}
                    >
                      <div
                        id="quill-reader"
                        ref={qlContainerRef}
                        className={`flex flex-col ql-editor quill-editor-custom quill-editor-custom-auto-scroll ${
                          hasOverflow ? 'justify-center' : 'justify-start'
                        }`}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Chỉ có image */}
              {hasImage && !hasDescription && (
                <div className="w-full flex items-center justify-center h-[87vh]">
                  <img
                    crossOrigin="anonymous"
                    src={buildingInfo?.extraInfo?.businessImageUrl}
                    alt="businessLogoImage"
                    style={{
                      width: 'auto',
                      height: '100%',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      objectPosition: 'center'
                    }}
                    loading="eager"
                    decoding="sync"
                    className="shadow-md"
                  />
                </div>
              )}

              {/* Chỉ có description */}
              {!hasImage && hasDescription && (
                <div className="w-full h-full">
                  <div
                    ref={elementRef}
                    className={`ql-container overflow-y-auto memo-container !h-auto pt-1 text-white ${
                      hasOverflow ? 'scrollbar-visible' : 'scrollbar-hidden'
                    }`}
                  >
                    <div
                      id="quill-reader"
                      ref={qlContainerRef}
                      className={`flex flex-col ql-editor quill-editor-custom quill-editor-custom-auto-scroll max-h-[87vh] ${
                        hasOverflow ? 'justify-center' : 'justify-start'
                      }`}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Spinner>
    </CustomModal>
  )
}
